import { Component } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

/**
 * Per-module error boundary. Catches JS errors in child tree and shows
 * a fallback UI instead of crashing the entire app.
 */
export class ModuleErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error(`[ErrorBoundary] ${this.props.moduleName || "Module"} crashed:`, error, info.componentStack);
  }

  handleReset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="flex items-center justify-center min-h-[60vh] p-8" data-testid="module-error-boundary">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="h-14 w-14 rounded-full bg-red-100 flex items-center justify-center mx-auto">
            <AlertTriangle className="h-7 w-7 text-red-600" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">
            {this.props.moduleName || "This module"} encountered an error
          </h2>
          <p className="text-sm text-muted-foreground">
            An unexpected error occurred. Your other modules are unaffected.
          </p>
          {this.state.error && (
            <pre className="text-xs bg-muted rounded-lg p-3 text-left overflow-auto max-h-32 text-muted-foreground">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex gap-3 justify-center">
            <button onClick={this.handleReset}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border bg-background hover:bg-muted transition" data-testid="error-retry-btn">
              <RefreshCw className="h-3.5 w-3.5" /> Retry
            </button>
            <a href="/"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition" data-testid="error-home-btn">
              <Home className="h-3.5 w-3.5" /> Dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Wrap any component with error boundary. Use as HOC:
 *   const SafePage = withErrorBoundary(MyPage, "Sales");
 */
export function withErrorBoundary(Component, moduleName) {
  return function WrappedWithBoundary(props) {
    return (
      <ModuleErrorBoundary moduleName={moduleName}>
        <Component {...props} />
      </ModuleErrorBoundary>
    );
  };
}
